import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AgenciesService } from '../../../services/agenciesService';
import { Agency, CreateAgencyFormData } from '../../../types';

// Query keys
export const agencyKeys = {
  all: ['agencies'] as const,
  lists: () => [...agencyKeys.all, 'list'] as const,
  details: () => [...agencyKeys.all, 'detail'] as const,
  detail: (id: string) => [...agencyKeys.details(), id] as const,
  current: () => [...agencyKeys.all, 'current'] as const,
  invoicePrefixes: () => [...agencyKeys.all, 'invoice-prefixes'] as const,
  adminOverview: () => [...agencyKeys.all, 'admin-overview'] as const,
};

export const useAdminOverview = () => {
  return useQuery({
    queryKey: agencyKeys.adminOverview(),
    queryFn: () => AgenciesService.getAdminOverview(),
    refetchInterval: 60_000,
  });
};

export const useUpdateHiddenEmails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (emails: string[]) => AgenciesService.updateHiddenEmails(emails),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.adminOverview() });
    },
  });
};

// React Query hooks
export const useAgencies = () => {
  return useQuery({
    queryKey: agencyKeys.lists(),
    queryFn: () => AgenciesService.getAgencies(),
  });
};

export const useAgency = (id: string) => {
  return useQuery({
    queryKey: agencyKeys.detail(id),
    queryFn: () => AgenciesService.getAgency(id),
    enabled: !!id,
  });
};

export const useCurrentAgency = (enabled: boolean = true) => {
  return useQuery({
    queryKey: agencyKeys.current(),
    queryFn: () => AgenciesService.getCurrentAgency(),
    enabled,
  });
};

export const useInvoicePrefixes = (enabled: boolean = true) => {
  return useQuery({
    queryKey: agencyKeys.invoicePrefixes(),
    queryFn: () => AgenciesService.getInvoicePrefixes(),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

export const useCreateAgency = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: AgenciesService.createAgency,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.lists() });
    },
  });
};

export const useUpdateAgency = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateAgencyFormData> }) =>
      AgenciesService.updateAgency(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agencyKeys.detail(data.id) });
    },
  });
};

export const useUpdateCurrentAgency = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: AgenciesService.updateCurrentAgency,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.current() });
      queryClient.invalidateQueries({ queryKey: agencyKeys.lists() });
    },
  });
};

export const useUploadCurrentAgencyLogo = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ logoFile, onProgress }: { logoFile: File; onProgress?: (progress: number) => void }) =>
      AgenciesService.uploadCurrentAgencyLogo(logoFile, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.current() });
      queryClient.invalidateQueries({ queryKey: agencyKeys.lists() });
    },
  });
};

export const useDeactivateAgency = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: AgenciesService.deactivateAgency,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agencyKeys.detail(data.id) });
    },
  });
};

export const useActivateAgency = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: AgenciesService.activateAgency,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agencyKeys.detail(data.id) });
    },
  });
};

export const useDeleteAgency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AgenciesService.deleteAgency,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.lists() });
    },
  });
};

export const useSendAgencyEmail = () => {
  return useMutation({
    mutationFn: ({ agencyId, subject, html }: { agencyId: string; subject: string; html: string }) =>
      AgenciesService.sendAgencyEmail(agencyId, subject, html),
  });
};

export const useUpdateInvoicePrefixes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (prefixes: Array<{ prefix: string; counter: number; label: string }>) =>
      AgenciesService.updateInvoicePrefixes(prefixes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.current() });
      queryClient.invalidateQueries({ queryKey: agencyKeys.lists() });
    },
  });
}; 